import { call, put } from 'redux-saga/effects';
import {
  axiosAuthInstance,
  API_END_POINTS,
} from 'api';
import { MESSAGES, TRANSFER_APPROVAL_STATUSES } from 'appconstants';

import {
  NOTIFICATION,
  LOADING,
  TRANSFER_APPROVAL,
} from '../actionTypes';

const getTeamMembers = () => (
  axiosAuthInstance.get(
    // eslint-disable-next-line
    `${API_END_POINTS.GET_MEMBERSHIPS}?page=1&size=1000&sortBy=user.givenName&direction=DESC&roleType.IN=Owner&roleType.IN=Administrator&roleType.IN=Transfer%20Approver`,
  )
);

const getOwner = () => (
  axiosAuthInstance.get(`${API_END_POINTS.GET_MEMBERSHIPS}?roleType.EQ=Owner`)
);

const getAllRules = (params) => (
  axiosAuthInstance.get(
    `${API_END_POINTS.TRANSFER_APPROVAL}?page=${params.pageNum}&size=${params.size}&sortBy=${params.sortBy}&direction=${params.direction}`,
  )
);

const addRule = (params) => (
  axiosAuthInstance.post(`${API_END_POINTS.TRANSFER_APPROVAL}`, params)
);

const modifyRule = (params) => (
  axiosAuthInstance.patch(`${API_END_POINTS.TRANSFER_APPROVAL}/${params.ruleGid}`, params.payload)
);

const removeRule = (params) => (
  axiosAuthInstance.delete(`${API_END_POINTS.TRANSFER_APPROVAL}/${params.ruleGid}`)
);

const getRuleDetail = (params) => (
  axiosAuthInstance.get(`${API_END_POINTS.TRANSFER_APPROVAL}/${params.ruleGid}`)
);

export function* fetchTransferApprovalRules(action) {
  try {
    yield put({
      type: LOADING.SET,
    });
    const size = 9;
    const response = yield call(getAllRules, {
      pageNum: action.payload.pageNum,
      size: action.payload?.size ?? size,
      sortBy: action.payload.sortBy,
      direction: action.payload.direction,
    });
    const tableData = response?.data?.entity?.content
    ?.map(item => ({
      gid: item.gid,
      amount: item.approvalAmount,
      authorizer: item.authorizers?.map(authorizer => ({
        name: `${authorizer.givenName} ${authorizer.familyName}`,
        userGid: authorizer.gid,
      })),
    })) ?? [];
    const getOwnerResponse = yield call(getOwner);
    const ownerUser = getOwnerResponse?.data?.entity?.content[0]?.user;
    const authorizerName = `${ownerUser.givenName} ${ownerUser.familyName}`;
    tableData.push({
      gid: 'AllFlags',
      amount: 'AllFlags',
      authorizer: [
        {
          name: authorizerName, // fetch the account owner info.
          userGid: ownerUser.gid,
        },
      ],
    });
    const totalElements = response?.data?.entity?.totalElements;
    yield put({
      type: TRANSFER_APPROVAL.SET_TRANSFER_RULES,
      payload: tableData,
    });
    yield put({
      type: TRANSFER_APPROVAL.SET_TRANSFER_RULES_TOTAL_ELEMENTS,
      payload: totalElements + 1,
    });
  } catch (e) {
    yield put({
      type: NOTIFICATION.ERROR,
      payload: e?.response?.data?.message ?? MESSAGES.TRANSFER_APPROVAL.FETCH_TRANSFER_APPROVALS_FAIL,
    });
  } finally {
    yield put({
      type: LOADING.UNSET,
    });
  }
}

export function* fetchTeamMembers(action) {
  try {
    yield put({
      type: LOADING.SET,
    });
    const response = yield call(getTeamMembers);
    const teamMembersContent = response?.data?.entity?.content;
    let teamMembers = [];
    if (Array.isArray(teamMembersContent) && teamMembersContent.length > 0) {
      teamMembers = teamMembersContent
        .map(item => ({
          label: `${item.user.givenName} ${item.user.familyName}`,
          value: item.user.gid,
        }));
    }
    yield put({
      type: TRANSFER_APPROVAL.SET_TEAM_MEMBERS,
      payload: teamMembers,
    });
  } catch (e) {
    yield put({
      type: NOTIFICATION.ERROR,
      payload: e?.response?.data?.message ?? MESSAGES.TRANSFER_APPROVAL.FETCH_TEAM_MEMBERS_FAIL,
    });
  } finally {
    yield put({
      type: LOADING.UNSET,
    });
  }
}

export function* addTransferApprovalRule(action) {
  try {
    yield put({
      type: LOADING.SET,
    });
    // call backend api here
    let response = null;
    if (!action.payload.editMode) {
      response = yield call(addRule, {
        approvalAmount: action.payload.amount,
        userGids: action.payload.approvedMembers.map(item => item.value),
      });
    } else {
      const payload = {};
      if (action.payload.amount !== null) {
        payload.approvalAmount = action.payload.amount;
      }
      payload.userGids = action.payload.approvedMembers.map(item => item.value);
      response = yield call(modifyRule, {
        ruleGid: action.payload.gid,
        payload,
      });
    }
    if (response?.data?.status !== 'SUCCESS') {
      throw new Error('Could not Modify the Rule');
    }
    yield put({
      type: NOTIFICATION.SUCCESS,
      payload: MESSAGES.TRANSFER_APPROVAL.ADD_NEW_RULE_SUCCESS,
    });
  } catch (e) {
    yield put({
      type: NOTIFICATION.ERROR,
      payload: e?.response?.data?.message ?? MESSAGES.TRANSFER_APPROVAL.ADD_NEW_RULE_FAIL,
    });
  } finally {
    yield put({
      type: LOADING.UNSET,
    });
    // re-fetch the api for transfer approval rules
    yield put({
      type: TRANSFER_APPROVAL.FETCH_TRANSFER_RULES,
      payload: {
        pageNum: action.payload.fetchRulesParams.pageNum ?? 1,
        sortBy: action.payload.fetchRulesParams.sortBy ?? 'approvalAmount',
        direction: action.payload.fetchRulesParams.direction ?? 'ASC',
      },
    });
  }
}

export function* deleteRule(action) {
  try {
    yield put({
      type: LOADING.SET,
    });
    // call api to delete the rule
    const response = yield call(removeRule, {
      ruleGid: action.payload.gid,
    });
    if (response?.data?.status !== 'SUCCESS') {
      throw new Error(response?.data?.message);
    }
    yield put({
      type: TRANSFER_APPROVAL.SET_STATUS,
      payload: {
        message: TRANSFER_APPROVAL_STATUSES.SUCCESS,
        type: TRANSFER_APPROVAL_STATUSES.DELETE_TYPE,
        value: action.payload.gid, // gid
      },
    });
    yield put({
      type: NOTIFICATION.SUCCESS,
      payload: MESSAGES.TRANSFER_APPROVAL.DELETE_RULE_SUCCESS,
    });
  } catch (e) {
    yield put({
      type: NOTIFICATION.ERROR,
      payload: e?.response?.data?.message ?? MESSAGES.TRANSFER_APPROVAL.DELETE_RULE_FAIL,
    });
    yield put({
      type: TRANSFER_APPROVAL.SET_STATUS,
      payload: {
        message: TRANSFER_APPROVAL_STATUSES.FAIL,
        type: TRANSFER_APPROVAL_STATUSES.DELETE_TYPE,
        value: action.payload.gid, // gid
      },
    });
  } finally {
    yield put({
      type: LOADING.UNSET,
    });
  }
}

export function* fetchExistingRule(action) {
  try {
    yield put({
      type: LOADING.SET,
    });
    // call api to fetch transfer center
    // approval details
    const response = yield call(getRuleDetail, {
      ruleGid: action.payload.gid,
    });
    if (response?.data?.status !== 'SUCCESS') {
      throw new Error(response?.data?.message);
    }
    const responseInformation = response?.data?.entity;
    yield put({
      type: TRANSFER_APPROVAL.SET_EXISTING_RULE,
      payload: {
        amount: responseInformation?.approvalAmount,
        approvedMembers: responseInformation?.authorizers
        ?.map(item => ({
          label: `${item.givenName} ${item.familyName}`,
          value: item.gid,
        }),
      ),
      },
    });
  } catch (e) {
    yield put({
      type: NOTIFICATION.ERROR,
      payload: e?.response?.data?.message ?? MESSAGES.TRANSFER_APPROVAL.DELETE_RULE_FAIL,
    });
  } finally {
    yield put({
      type: LOADING.UNSET,
    });
  }
}
